import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import {Select, ColorPicker} from "antd";
import HttpClient from "./HttpClient";
import {SystemColors} from "@/helpers/UserAnnotationColorPickerComponent";

const {Option} = Select;

const AntdCategorySelect = (props) => {
    const [categories, setCategories] = useState([]);
    const [errors, setErrors] = useState('');
    const [newCategory, setNewCategory] = useState(null);
    const [openColorPicker, setOpenColorPicker] = useState(null);
    const [selectOpen, setSelectOpen] = useState(false);

    useEffect(() => {
        fetchCategories();
        return () => {
            setOpenColorPicker(null);
            setSelectOpen(false);
        }
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await HttpClient.get('/annotation-categories');
            setCategories(response.data.categories.map(cat => ({label: cat.name, value: cat.name, color: cat.color})));
        } catch (error) {
            console.error('Error fetching categories:', error);
            setErrors('Failed to fetch categories');
        }
    };

    const onChangeHandler = useCallback((value, option) => {
        if (option.isNew) {
            const addedCategory = {label: option.value, value: option.value, color: '#000000', isNew: true};
            setCategories(prev => [...prev.filter(cat => !cat.isNew), addedCategory]);
            setNewCategory(null);
        }

        props.onChangeCallback({target: {name: props.name, value: option.value, color: option.color}});
        setSelectOpen(false);
    }, [props.name, props.onChangeCallback]);

    const handleColorChange = useCallback((color, categoryValue) => {
        setCategories(prev => prev.map(cat =>
            cat.value === categoryValue ? {...cat, color: color.toHexString()} : cat
        ));

        if (categoryValue === props.value) {
            props.onChangeCallback({target: {name: props.name, value: categoryValue, color: color.toHexString()}});
        }
    }, [props.value, props.name, props.onChangeCallback]);

    const CustomOption = React.memo(({value, label, color}) => {
        return (
            <div className="flex justify-between items-center w-full">
                <span className="w-full" onClick={() => onChangeHandler(value, {value, label, color})}>
                    {label}
                </span>
                <ColorPicker
                    open={openColorPicker === value}
                    value={color}
                    onChange={(newColor) => handleColorChange(newColor, value)}
                    onOpenChange={(open) => {
                        setOpenColorPicker(open ? value : null);
                        if (open) {
                            setSelectOpen(true);
                        }
                    }}
                    size="small"
                    presets={[{label: 'Recommended', colors: SystemColors}]}
                />
            </div>
        );
    });

    const SelectedOption = React.memo(({value, label, color = '#000000'}) => (
        <div className="flex items-center max-w-[30px] gaa-category-selected-option pointer-events-none">
            <div
                className="w-4 h-4 rounded mr-2"
                style={{backgroundColor: color}}
            />
            <span className="max-w-[1px]">{label}</span>
        </div>
    ));

    const handleSearch = useCallback((value, option) => {
        if (value && !categories.some(cat => cat.value.toLowerCase() === value.toLowerCase())) {
            setNewCategory({label: `Create "${value}"`, value: value, color: '#000000', isNew: true});
        } else {
            setNewCategory(null);
        }
    }, [categories]);

    const allOptions = useMemo(() => [...categories, ...(newCategory ? [newCategory] : [])], [categories, newCategory]);

    return (
        <>
            <Select
                size="middle"
                className="w-full h-[38px]"
                disabled={props.disabled}
                value={props.value ? props.value : null}
                mode={props.multiple ? "multiple" : undefined}
                onChange={onChangeHandler}
                onSearch={handleSearch}
                placeholder={props.placeholder || 'Write category *'}
                showSearch
                open={selectOpen}
                onDropdownVisibleChange={(visible) => {
                    if (!openColorPicker) {
                        setSelectOpen(visible);
                    }
                }}
                dropdownRender={(menu) => (
                    <div onMouseDown={(e) => e.preventDefault()}>
                        {menu}
                    </div>
                )}
                labelInValue
                optionLabelProp="label"
            >
                {allOptions.map((cat) => (
                    <Option
                        key={cat.value}
                        value={cat.value}
                        label={<SelectedOption {...cat} />}
                        isNew={cat.isNew}
                        color={cat.color}
                    >
                        <CustomOption {...cat} />
                    </Option>
                ))}
            </Select>
            {errors && <div className="text-red-500 mt-1">{errors}</div>}
        </>
    );
};

export default React.memo(AntdCategorySelect);
